import React, { useState } from 'react';
import { gameLogger } from '../../../../utils/GameLogger';
import { windowBridge } from '../../framework/windowBridge';
import { DEBUG_TOOLBOX_BUTTON_SIZE } from '../../framework/WindowLauncher';
import { useWindow } from '../../framework/WindowManagerContext';
import { COLORS } from '../../theme';
import { isMapPickerAvailable } from '../../Overlays/MapPickerOverlay';
import {
  isRaceDebugOverlayEnabled,
  toggleRaceDebugOverlay,
} from '../../../systems/race/RaceDebugOverlayState';

interface DebugToolboxProps {
  onAction: (action: string, data?: any) => void;
  isDevToolsActive?: boolean;
  isAbilitiesActive?: boolean;
  isMultiplayerLabelsActive?: boolean;
}

export const DebugToolbox: React.FC<DebugToolboxProps> = ({
  onAction,
  isDevToolsActive = false,
  isAbilitiesActive = false,
  isMultiplayerLabelsActive = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [raceDebugActive, setRaceDebugActive] = useState(isRaceDebugOverlayEnabled());
  // Engine open-state for the windows this bar toggles directly (HUD fold,
  // 2026-06-12): the registry chrome + WindowHost own the windows; this bar
  // is just a launcher with live active-dots.
  const tracker = useWindow('projectileTracker');
  const simulationTools = useWindow('simulationTools');
  const cameraControl = useWindow('cameraControl');
  const mapTools = useWindow('mapTools');
  const mapPicker = useWindow('mapPicker');

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          color: '#aaa',
          borderRadius: '6px',
          // Shared with the cluster anchor derivation (WindowLauncher.tsx) so
          // the chip's box size can never drift from the overlap contract.
          width: DEBUG_TOOLBOX_BUTTON_SIZE,
          height: DEBUG_TOOLBOX_BUTTON_SIZE,
          padding: '0',
          fontSize: '14px',
          cursor: 'pointer',
          pointerEvents: 'auto'
        }}
        title="Expand Debug Toolbox"
      >
        🛠️
      </button>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      background: 'rgba(0, 0, 0, 0.8)',
      padding: '6px',
      borderRadius: '4px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      pointerEvents: 'auto',
      minWidth: '32px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '4px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        paddingBottom: '2px'
      }}>
        <span style={{ fontSize: '10px', color: '#888', fontWeight: 'bold' }}>DEV BAR</span>
        <button
          onClick={() => setIsExpanded(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#888',
            cursor: 'pointer',
            fontSize: '10px',
            padding: '0 2px'
          }}
        >
          ✕
        </button>
      </div>

      <ToolButton
        icon="🚀"
        label="Tracker"
        active={tracker.open}
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] windowBridge.toggle: projectileTracker');
          tracker.toggle();
        }}
        color="#2ecc71"
      />

      <ToolButton
        icon="🏷️"
        label="MP Labels"
        active={isMultiplayerLabelsActive}
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] action: toggle_multiplayer_labels');
          onAction('toggle_multiplayer_labels');
        }}
        color="#22c55e"
      />

      <ToolButton
        icon="🔧"
        label="DevTools"
        active={isDevToolsActive}
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] action: toggle_devtools');
          onAction('toggle_devtools');
        }}
        color="#3498db"
      />

      <ToolButton
        icon="✨"
        label="Abilities"
        active={isAbilitiesActive}
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] action: toggle_abilities');
          onAction('toggle_abilities');
        }}
        color="#fbbf24"
      />

      <ToolButton
        icon="✏️"
        label="Editor"
        onClick={() => {
          // Pure editor-mode toggle (DJ ruling 2026-06-12) — the old path
          // ALSO opened an EditorToolsWindow that entering editor mode
          // immediately unmounted (HUD hides in editor mode). Window deleted.
          gameLogger.debug('ui', '[DebugToolbox] action: toggle_editor');
          onAction('toggle_editor');
        }}
        color="#f1c40f"
      />

      <ToolButton
        icon="🧪"
        label="Simulation"
        active={simulationTools.open}
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] windowBridge.toggle: simulationTools');
          simulationTools.toggle();
        }}
        color="#00ffff"
      />

      <ToolButton
        icon="📷"
        label="Camera"
        active={cameraControl.open}
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] windowBridge.toggle: cameraControl');
          cameraControl.toggle();
        }}
        color="#e91e63"
      />

      <ToolButton
        icon="🗺️"
        label="Map Info"
        active={mapTools.open}
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] windowBridge.toggle: mapTools');
          mapTools.toggle();
        }}
        title="Map Info Window"
      />

      {/* Dev surface only: not on the public game domain (?debugToolbox=1
          can show this bar there). M is the World Map, not the picker. */}
      {isMapPickerAvailable() && (
        <ToolButton
          icon="🧭"
          label="Map Picker"
          active={mapPicker.open}
          onClick={() => {
            gameLogger.debug('ui', '[DebugToolbox] windowBridge.toggle: mapPicker');
            mapPicker.toggle();
          }}
          title="Map Picker overlay (dev warp list)"
        />
      )}

      <ToolButton
        icon='🔊'
        label='Sound Debug'
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] windowBridge.toggle: gmSoundDebug');
          windowBridge.toggle('gmSoundDebug');
        }}
        color='#9b59b6'
      />

      <ToolButton
        icon="📋"
        label="QA Panel"
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] windowBridge.toggle: qaPanel');
          windowBridge.toggle('qaPanel');
        }}
        color="#f39c12"
      />

      <ToolButton
        icon="🟢"
        label="Contracts"
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] windowBridge.toggle: contractDashboard');
          windowBridge.toggle('contractDashboard');
        }}
        color="#2ecc71"
      />

      <ToolButton
        icon="🏦"
        label="Bank Vault"
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] windowBridge.toggle: bank');
          windowBridge.toggle('bank');
        }}
        color="#a55eea"
      />

      <ToolButton
        icon={'\uD83D\uDCDC'}
        label='Combat Log'
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] action: toggle_combat_log');
          onAction('toggle_combat_log');
        }}
        color='#e67e22'
      />

      <ToolButton
        icon={'\uD83D\uDD27'}
        label='Diagnostics'
        onClick={() => {
          gameLogger.debug('ui', '[DebugToolbox] action: open_diagnostics');
          window.open('/diagnostic', '_blank');
        }}
        color='#e74c3c'
      />

      <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '2px 0' }} />

      <ToolButton
        icon="🏁"
        label="Race Debug"
        active={raceDebugActive}
        onClick={() => {
          const next = toggleRaceDebugOverlay();
          setRaceDebugActive(next);
          gameLogger.debug('ui', `[DebugToolbox] race debug overlay: ${next ? 'ON' : 'OFF'}`);
        }}
        color="#2ecc71"
        title="Toggle GM Race debug overlay (phase, hazards, powerups, event log)"
      />

      <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '2px 0' }} />

      <ToolButton
        icon="🛑"
        label="PAUSE"
        onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'F8', bubbles: true }))}
        color="#e74c3c"
        variant="danger"
      />

    </div>
  );
};

interface ToolButtonProps {
  icon: string;
  label: string;
  onClick: () => void;
  active?: boolean;
  color?: string;
  variant?: 'normal' | 'danger';
  title?: string;
}

const ToolButton: React.FC<ToolButtonProps> = ({ icon, label, onClick, active, color = '#fff', variant = 'normal', title }) => {
  const [hover, setHover] = useState(false);
  const dangerBg = variant === 'danger' ? `${COLORS.danger}33` : undefined;
  const dangerBorder = variant === 'danger' ? `1px solid ${COLORS.danger}` : undefined;

  const baseStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: active ? `${color}44` : dangerBg ?? (hover ? 'rgba(255,255,255,0.1)' : 'transparent'),
    border: active ? `1px solid ${color}` : dangerBorder ?? (hover ? '1px solid rgba(255,255,255,0.2)' : '1px solid transparent'),
    borderRadius: '3px',
    padding: '4px 6px',
    cursor: 'pointer',
    color: active ? '#fff' : (hover ? '#fff' : '#ccc'),
    fontSize: '11px',
    width: '100%',
    transition: 'all 0.1s',
    outline: 'none'
  };

  return (
    <button
      onClick={onClick}
      style={baseStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={title || label}
    >
      <span style={{ fontSize: '14px' }}>{icon}</span>
      <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
      {active && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color }} />}
    </button>
  );
};
